import './styles.css';

import type {
  Commit,
  LicenseInfo,
  ModalId,
  PromptRepo,
  Settings,
  TabId,
  TestCase,
  Toast,
} from './lib/types';
import { estimateTokens, tokenStats } from './lib/tokenizer';
import { diffWords, diffStats, renderDiffHtml } from './lib/diff';
import { buildCostTable, formatUsd, formatUsdFull } from './lib/pricing';
import { runSuite, compareRuns } from './lib/runner';
import { extractVariables, interpolate, interpolatePreview } from './lib/interpolate';
import { generateTypeScript, generatePython, exportFilename } from './lib/exporter';
import { makeAssertion } from './lib/assertions';
import {
  uid,
  escapeHtml,
  downloadFile,
  copyToClipboard,
  formatDateTime,
} from './lib/util';

const STORAGE_KEY = 'promptvault:state:v1';
const STARTER_PROMPT = `You are a precise support-triage assistant.

Task: {{user_query}}
Context: {{context_json}}

Return valid JSON with keys: answer, priority, confidence.
Keep the reply under 5 sentences and never apologize unnecessarily.`;

interface PersistedState {
  repos: PromptRepo[];
  commits: Commit[];
  cases: TestCase[];
  settings: Settings;
  license: LicenseInfo;
  selectedRepoId: string | null;
  activeTab: TabId;
}

const state: {
  repos: PromptRepo[];
  commits: Commit[];
  cases: TestCase[];
  settings: Settings;
  license: LicenseInfo;
  selectedRepoId: string | null;
  activeTab: TabId;
  modal: ModalId;
  toast: Toast | null;
  editorContent: string;
  editorMessage: string;
  editorBaseCommitId: string | null;
  historyLeftId: string | null;
  historyRightId: string | null;
  testResults: Record<string, Awaited<ReturnType<typeof runSuite>>> | null;
  draftAssertions: ReturnType<typeof makeAssertion>[];
  estimatorOutputTokens: number | null;
  runProgress: { completed: number; total: number } | null;
  running: boolean;
} = {
  repos: [],
  commits: [],
  cases: [],
  settings: defaultSettings(),
  license: { tier: 'free' },
  selectedRepoId: null,
  activeTab: 'editor',
  modal: null,
  toast: null,
  editorContent: '',
  editorMessage: 'Working copy',
  editorBaseCommitId: null,
  historyLeftId: null,
  historyRightId: null,
  testResults: null,
  draftAssertions: [],
  estimatorOutputTokens: null,
  runProgress: null,
  running: false,
};

function defaultSettings(): Settings {
  return {
    provider: 'mock',
    apiKeys: { openai: '', anthropic: '', gemini: '' },
    models: { openai: 'gpt-4o', anthropic: 'claude-3-5-haiku-latest', gemini: 'gemini-2.5-flash' },
    outputTokens: 250,
    simulateLatency: false,
  };
}

function loadState(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as PersistedState;
    state.repos = parsed.repos ?? [];
    state.commits = parsed.commits ?? [];
    state.cases = parsed.cases ?? [];
    state.settings = parsed.settings ?? defaultSettings();
    state.license = parsed.license ?? { tier: 'free' };
    state.selectedRepoId = parsed.selectedRepoId ?? null;
    state.activeTab = parsed.activeTab ?? 'editor';
  } catch {
    // ignore corrupt storage
  }
}

function saveState(): void {
  try {
    const snapshot: PersistedState = {
      repos: state.repos,
      commits: state.commits,
      cases: state.cases,
      settings: state.settings,
      license: state.license,
      selectedRepoId: state.selectedRepoId,
      activeTab: state.activeTab,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore
  }
}

function isPro(): boolean {
  return state.license.tier === 'pro';
}

function reposForDisplay(): PromptRepo[] {
  return [...state.repos].sort((a, b) => b.updatedAt - a.updatedAt);
}

function commitsFor(repoId: string): Commit[] {
  return state.commits.filter((c) => c.repoId === repoId).sort((a, b) => a.version - b.version);
}

function latestCommit(repoId: string): Commit | undefined {
  const commits = commitsFor(repoId);
  return commits.length ? commits[commits.length - 1] : undefined;
}

function casesFor(repoId: string): TestCase[] {
  return state.cases.filter((c) => c.repoId === repoId).sort((a, b) => a.createdAt - b.createdAt);
}

function selectedRepo(): PromptRepo | undefined {
  return state.repos.find((r) => r.id === state.selectedRepoId);
}

function commitById(id: string | null): Commit | undefined {
  if (!id) return undefined;
  return state.commits.find((c) => c.id === id);
}

function toast(message: string, kind: Toast['kind'] = 'info'): void {
  state.toast = { id: uid('toast'), message, kind };
  render();
  const id = state.toast.id;
  setTimeout(() => {
    if (state.toast?.id === id) {
      state.toast = null;
      render();
    }
  }, 3200);
}

function openModal(modal: ModalId): void {
  state.modal = modal;
  render();
}

function closeModal(): void {
  state.modal = null;
  render();
}

function setActiveTab(tab: TabId): void {
  state.activeTab = tab;
  saveState();
  if (tab === 'tests') state.testResults = null;
  render();
}

function qs<T extends Element>(selector: string, root: ParentNode = document): T {
  const node = root.querySelector(selector);
  if (!node) throw new Error(`Missing element: ${selector}`);
  return node as T;
}

function maybe<T extends Element>(selector: string, root: ParentNode = document): T | null {
  return root.querySelector(selector) as T | null;
}

function chip(label: string, cls = 'chip'): string {
  return `<span class="${cls}">${escapeHtml(label)}</span>`;
}

const tabs: { id: TabId; label: string }[] = [
  { id: 'editor', label: 'Editor' },
  { id: 'history', label: 'History & Diff' },
  { id: 'estimator', label: 'Estimator' },
  { id: 'tests', label: 'Tests' },
  { id: 'export', label: 'Export' },
];

function render(): void {
  const app = qs<HTMLDivElement>('#app');
  app.innerHTML = `
    <div class="min-h-screen flex flex-col">
      ${headerHtml()}
      <main class="mx-auto w-full max-w-7xl px-4 py-6 flex-1">
        ${tabContentHtml()}
      </main>
      ${modalHtml()}
      ${toastHtml()}
    </div>
  `;
  wire();
}

function headerHtml(): string {
  const repoOptions = reposForDisplay()
    .map((r) => `<option value="${r.id}" ${r.id === state.selectedRepoId ? 'selected' : ''}>${escapeHtml(r.name)}</option>`)
    .join('');
  const licenseBadge = isPro()
    ? '<span class="badge bg-emerald-500/20 text-emerald-300">Pro</span>'
    : '<span class="badge bg-slate-500/20 text-slate-400">Free</span>';

  const navButtons = tabs
    .map(
      (t) => `<button data-tab="${t.id}" class="tab-btn ${t.id === state.activeTab ? 'tab-btn-active' : ''}">${t.label}</button>`,
    )
    .join('');

  return `
    <header class="border-b border-slate-800/80 bg-slate-900/60 backdrop-blur sticky top-0 z-30">
      <div class="mx-auto max-w-7xl px-4 py-3 flex flex-wrap items-center gap-3">
        <div class="flex items-center gap-2 font-semibold text-white">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" class="text-indigo-400"><path d="M12 2 20 7v10l-8 5-8-5V7l8-5Z" stroke="currentColor" stroke-width="1.6"/><path d="M12 22v-10" stroke="currentColor" stroke-width="1.6"/></svg>
          PromptVault Git
        </div>
        <select id="repoSelect" class="field max-w-[200px] py-1.5 text-sm">${repoOptions || '<option value="">— no repos —</option>'}</select>
        <button id="btnNewRepo" class="btn-primary py-1.5 text-sm">+ Repo</button>
        <nav class="flex items-center gap-1 bg-slate-900/60 rounded-lg p-0.5 border border-slate-800">${navButtons}</nav>
        <div class="ml-auto flex items-center gap-2">
          ${licenseBadge}
          <button id="btnProvider" class="btn-ghost py-1.5 text-sm">Provider</button>
          <button id="btnLicense" class="btn-ghost py-1.5 text-sm">License</button>
        </div>
      </div>
    </header>
  `;
}

function tabContentHtml(): string {
  switch (state.activeTab) {
    case 'editor':
      return editorTabHtml();
    case 'history':
      return historyTabHtml();
    case 'estimator':
      return estimatorTabHtml();
    case 'tests':
      return testsTabHtml();
    case 'export':
      return exportTabHtml();
    default:
      return '';
  }
}

function emptyRepoStateHtml(message = 'Select or create a prompt repo to begin.'): string {
  return `
    <div class="panel p-10 text-center max-w-2xl mx-auto">
      <h2 class="text-xl font-semibold text-white mb-2">No repo selected</h2>
      <p class="text-slate-400 mb-4">${escapeHtml(message)}</p>
      <button id="btnEmptyNewRepo" class="btn-primary">Create your first repo</button>
    </div>
  `;
}

function editorTabHtml(): string {
  const repo = selectedRepo();
  if (!repo) return emptyRepoStateHtml();

  if (state.editorBaseCommitId === null) {
    const lc = latestCommit(repo.id);
    state.editorContent = lc?.content ?? '';
    state.editorBaseCommitId = lc?.id ?? null;
    state.editorMessage = 'Working copy';
  }

  const vars = extractVariables(state.editorContent);
  const ts = tokenStats(state.editorContent);

  return `
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div class="panel p-4 flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-lg font-semibold text-white">${escapeHtml(repo.name)}</h2>
            <p class="text-xs text-slate-500">${escapeHtml(repo.description || 'No description')}</p>
          </div>
          <span class="text-xs font-mono text-slate-400">base: ${escapeHtml(latestCommit(repo.id)?.version ? `v${latestCommit(repo.id)!.version}` : '—')}</span>
        </div>
        <div>
          <label class="label">Prompt content</label>
          <textarea id="editorContent" class="field h-72 font-mono">${escapeHtml(state.editorContent)}</textarea>
        </div>
        <div>
          <label class="label">Commit message</label>
          <input id="editorMessage" class="field" value="${escapeHtml(state.editorMessage)}" />
        </div>
        <div class="flex flex-wrap gap-2">
          <button id="btnSaveCommit" class="btn-primary">Commit new version</button>
          <button id="btnResetEditor" class="btn-ghost">Reset to latest</button>
        </div>
        <div id="editorTokenSummary" class="text-xs text-slate-400">Tokens: ${ts.tokens} · Chars: ${ts.chars} · Words: ${ts.words}</div>
        <div id="editorVars" class="flex flex-wrap gap-1"></div>
      </div>
      <div class="panel p-4 flex flex-col gap-3">
        <div>
          <h3 class="text-sm font-semibold text-slate-200 mb-2">Live preview (unresolved slots shown as ‹name›)</h3>
          <div id="editorPreview" class="code-block panel p-3 bg-slate-950/60 min-h-[120px]"></div>
        </div>
        <div>
          <h3 class="text-sm font-semibold text-slate-200 mb-2">Test input values (JSON)</h3>
          <textarea id="editorTestValues" class="field h-32 font-mono" placeholder='{"user_query":"...","context_json":"..."}'>${escapeHtml('{}')}</textarea>
          <button id="btnPreviewWithValues" class="btn-ghost mt-2 text-xs">Preview with values</button>
          <div id="editorPreviewVals" class="code-block panel p-3 bg-slate-950/60 mt-2 min-h-[80px]"></div>
        </div>
        <div class="text-xs text-slate-500">${vars.length ? `Variables: ${vars.map((v) => `{{${v}}}`).join(' ')}` : 'No {{variables}} detected.'}</div>
      </div>
    </div>
  `;
}

function historyTabHtml(): string {
  const repo = selectedRepo();
  if (!repo) return emptyRepoStateHtml();
  const commits = commitsFor(repo.id);
  if (!commits.length) return emptyRepoStateHtml('No commits yet.');

  if (!state.historyLeftId || !commits.find((c) => c.id === state.historyLeftId)) {
    state.historyLeftId = commits[Math.max(0, commits.length - 2)]?.id ?? commits[0].id;
  }
  if (!state.historyRightId || !commits.find((c) => c.id === state.historyRightId)) {
    state.historyRightId = commits[commits.length - 1].id;
  }

  const optionsHtml = (selectedId: string | null) =>
    commits
      .map((c) => `<option value="${c.id}" ${c.id === selectedId ? 'selected' : ''}>v${c.version} — ${escapeHtml(c.message)}</option>`)
      .join('');

  const left = commitById(state.historyLeftId);
  const right = commitById(state.historyRightId);

  let diffHtml = '<div class="text-slate-500 text-sm">Pick two versions to compare.</div>';
  let statsHtml = '';
  if (left && right) {
    const ops = diffWords(left.content, right.content);
    const stats = diffStats(ops);
    diffHtml = `<div class="code-block">${renderDiffHtml(ops)}</div>`;
    statsHtml = `
      <div class="flex flex-wrap gap-3 text-xs">
        <span class="chip-green">+${stats.added} words</span>
        <span class="chip-red">-${stats.removed} words</span>
        <span class="chip">unchanged ${stats.unchanged}</span>
        <span class="chip">similarity ${stats.similarity}%</span>
      </div>
    `;
  }

  return `
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div class="panel p-4 lg:col-span-1">
        <h3 class="text-sm font-semibold text-white mb-2">Commit history</h3>
        <div class="space-y-2 scroll-y max-h-[500px]">
          ${commits
            .slice()
            .reverse()
            .map(
              (c) => `<div class="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                <div class="flex items-center justify-between">
                  <span class="font-mono text-emerald-300 font-semibold">v${c.version}</span>
                  <span class="text-[11px] text-slate-500">${formatDateTime(c.createdAt)}</span>
                </div>
                <div class="text-sm text-slate-300 mt-1">${escapeHtml(c.message)}</div>
                <pre class="text-[11px] text-slate-500 mt-1 line-clamp-3 whitespace-pre-wrap">${escapeHtml(c.content.slice(0, 240))}</pre>
              </div>`,
            )
            .join('')}
        </div>
      </div>
      <div class="panel p-4 lg:col-span-2 flex flex-col gap-3">
        <div class="flex flex-wrap items-end gap-3">
          <div class="flex-1 min-w-[200px]">
            <label class="label">Before (old)</label>
            <select id="historyLeft" class="field">${optionsHtml(state.historyLeftId)}</select>
          </div>
          <div class="flex-1 min-w-[200px]">
            <label class="label">After (new)</label>
            <select id="historyRight" class="field">${optionsHtml(state.historyRightId)}</select>
          </div>
          <button id="btnRenderDiff" class="btn-primary">Diff</button>
        </div>
        ${statsHtml}
        <div id="diffViewer" class="scroll-y rounded-lg border border-slate-800 bg-slate-950/60 p-4 max-h-[480px]">${diffHtml}</div>
      </div>
    </div>
  `;
}

function estimatorTabHtml(): string {
  const defaultText = selectedRepo() ? (latestCommit(selectedRepo()!.id)?.content ?? '') : STARTER_PROMPT;
  const savedOutput = state.estimatorOutputTokens ?? state.settings.outputTokens;
  state.estimatorOutputTokens = savedOutput;

  return `
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div class="panel p-4 lg:col-span-1 flex flex-col gap-3">
        <h3 class="text-sm font-semibold text-white">Input</h3>
        <textarea id="estimatorPrompt" class="field h-64 font-mono">${escapeHtml(defaultText)}</textarea>
        <div>
          <label class="label">Assumed output tokens per call</label>
          <input id="estimatorOutput" type="number" min="0" class="field" value="${savedOutput}" />
        </div>
        <div id="estimatorBreakdown" class="text-xs text-slate-400"></div>
      </div>
      <div class="panel p-4 lg:col-span-2">
        <h3 class="text-sm font-semibold text-white mb-2">Live cost comparison</h3>
        <div id="estimatorTable" class="scroll-y max-h-[520px]"></div>
      </div>
    </div>
  `;
}

function testsTabHtml(): string {
  const repo = selectedRepo();
  if (!repo) return emptyRepoStateHtml();

  const commits = commitsFor(repo.id);
  const vars = extractVariables(latestCommit(repo.id)?.content ?? '');
  const cases = casesFor(repo.id);

  const sampleInputs = vars
    .map((v) => `<div>
       <label class="label">${escapeHtml(v)}</label>
       <input class="field test-var" data-var="${escapeHtml(v)}" placeholder="value for ${escapeHtml(v)}" />
     </div>`)
    .join('') || '<p class="text-xs text-slate-500">No variables detected in latest commit.</p>';

  let resultsHtml = '';
  if (state.testResults) {
    const commitIds = Object.keys(state.testResults);
    const renderedResults = commitIds
      .map((cid) => {
        const result = (state.testResults as any)[cid];
        const caseRows = result.rows
          .map((row: any) => `
            <tr>
              <td class="table-td font-medium">${escapeHtml(row.caseName)}</td>
              <td class="table-td">${row.pass ? '<span class="chip-green">PASS</span>' : '<span class="chip-red">FAIL</span>'}</td>
              <td class="table-td text-xs">
                ${row.results.map((r: any) => `<div class="${r.pass ? 'text-emerald-300' : 'text-rose-300'}">${escapeHtml(r.label)} — ${escapeHtml(r.detail)}</div>`).join('') || '<span class="text-slate-500">no assertions</span>'}
              </td>
              <td class="table-td text-xs text-slate-500">${row.latencyMs}ms</td>
            </tr>
            <tr><td colspan="4" class="table-td"><details><summary class="cursor-pointer text-xs text-slate-400">output</summary><pre class="code-block mt-2">${escapeHtml(row.output || row.error || '')}</pre></details></td></tr>
          `)
          .join('');
        return `
          <div class="panel p-4">
            <div class="flex items-center justify-between mb-2">
              <h4 class="text-sm font-semibold text-white">Commit v${result.version}</h4>
              <span class="text-sm font-mono ${result.score >= 80 ? 'text-emerald-300' : result.score >= 50 ? 'text-amber-300' : 'text-rose-300'}">${result.passed}/${result.total} Passed · ${result.score}%</span>
            </div>
            <table class="w-full"><thead><tr>
              <th class="table-th">Case</th><th class="table-th">Result</th><th class="table-th">Assertions</th><th class="table-th">Latency</th>
            </tr></thead><tbody>${caseRows}</tbody></table>
          </div>
        `;
      })
      .join('');

    let compareHtml = '';
    if (commitIds.length === 2) {
      const left = (state.testResults as any)[commitIds[0]];
      const right = (state.testResults as any)[commitIds[1]];
      const summary = compareRuns(left, right);
      compareHtml = `
        <div class="panel p-4">
          <h4 class="text-sm font-semibold text-white mb-2">A/B comparison</h4>
          <p class="text-sm">Winner: <strong class="${summary.winner === 'right' ? 'text-emerald-300' : summary.winner === 'left' ? 'text-rose-300' : 'text-slate-300'}">${summary.winner === 'tie' ? 'Tie' : summary.winner === 'right' ? `v${right.version} (right)` : `v${left.version} (left)`}</strong> · Δ assertions ${summary.deltaAssertions >= 0 ? '+' : ''}${summary.deltaAssertions} · Δ score ${summary.deltaScore >= 0 ? '+' : ''}${summary.deltaScore}</p>
        </div>
      `;
    }

    resultsHtml = `
      <div class="space-y-4 mt-4">
        <div class="grid grid-cols-1 ${commitIds.length === 2 ? 'lg:grid-cols-2' : ''} gap-4">${renderedResults}</div>
        ${compareHtml}
      </div>
    `;
  }

  const runStatus = state.running
    ? `<span class="text-sm text-indigo-300">Running… ${state.runProgress?.completed ?? 0}/${state.runProgress?.total ?? 0}</span>`
    : '';

  const assertedList = state.draftAssertions.length
    ? state.draftAssertions
        .map((a, i) => `<div class="chip" data-idx="${i}">${escapeHtml(a.type)} <button class="text-rose-300 ml-1" data-remove-assertion="${i}">×</button></div>`)
        .join(' ')
    : '<span class="text-xs text-slate-500">No assertions attached yet.</span>';

  return `
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div class="panel p-4 flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold text-white">Test cases (${cases.length})</h3>
          <span class="text-xs text-slate-500">${isPro() ? 'Pro: unlimited' : 'Free: 5 max'}</span>
        </div>
        <div class="scroll-y max-h-[480px] space-y-3">
          ${cases.length ? cases.map((c) => testcaseCardHtml(c)).join('') : '<p class="text-sm text-slate-500">No test cases yet.</p>'}
        </div>
      </div>
      <div class="panel p-4 flex flex-col gap-3">
        <h3 class="text-sm font-semibold text-white">New test case</h3>
        <input id="caseName" class="field" placeholder="Case name, e.g. Billing query" />
        <div class="space-y-2">${sampleInputs}</div>
        <div>
          <label class="label">Assertions</label>
          <div id="assertionList" class="flex flex-wrap gap-1 mb-2">${assertedList}</div>
          <select id="assertionType" class="field">
            <option value="json">Must be valid JSON</option>
            <option value="json-keys">JSON must contain keys</option>
            <option value="not-regex">Must NOT match regex</option>
            <option value="word-count">Word count between X and Y</option>
          </select>
          <button id="btnAddAssertion" class="btn-ghost mt-2 text-xs">+ Attach assertion</button>
        </div>
        <button id="btnAddCase" class="btn-primary">Add test case</button>
      </div>
    </div>

    <div class="panel p-4 mt-4">
      <div class="flex flex-wrap items-end gap-3">
        <div class="flex-1 min-w-[200px]">
          <label class="label">Commit to run</label>
          <select id="runCommit" class="field">
            ${commits.map((c) => `<option value="${c.id}">v${c.version} — ${escapeHtml(c.message)}</option>`).join('')}
          </select>
        </div>
        <label class="flex items-center gap-2 text-sm text-slate-300">
          <input id="runCompare" type="checkbox" ${isPro() ? '' : 'disabled'} />
          Compare with previous commit (A/B) ${isPro() ? '' : '<span class="text-xs text-slate-500">(Pro)</span>'}
        </label>
        <button id="btnRunTests" class="btn-primary" ${state.running ? 'disabled' : ''}>Run suite</button>
        ${runStatus}
      </div>
      ${resultsHtml}
    </div>
  `;
}

function testcaseCardHtml(tc: TestCase): string {
  return `
    <div class="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
      <div class="flex items-center justify-between">
        <span class="text-sm font-semibold text-white">${escapeHtml(tc.name)}</span>
        <button class="btn-danger text-xs py-1" data-remove-case="${tc.id}">Remove</button>
      </div>
      <div class="text-xs text-slate-400 mt-1">${Object.entries(tc.values).map(([k, v]) => `${escapeHtml(k)}=${escapeHtml(v)}`).join(', ') || 'no variables'}</div>
      <div class="flex flex-wrap gap-1 mt-1">${(tc.assertions ?? []).map((a) => chip(a.type)).join(' ') || '<span class="text-xs text-slate-500">no assertions</span>'}</div>
    </div>
  `;
}

function exportTabHtml(): string {
  const repo = selectedRepo();
  if (!repo) return emptyRepoStateHtml();
  const commits = commitsFor(repo.id);
  if (!commits.length) return emptyRepoStateHtml('No commits yet.');
  const currentExportCommitId = (state as any).exportCommitId as string | null;
  if (!currentExportCommitId || !commitById(currentExportCommitId)) {
    (state as any).exportCommitId = commits[commits.length - 1].id;
  }
  if ((state as any).exportLang === undefined) (state as any).exportLang = 'typescript';

  const selectedId = (state as any).exportCommitId as string;
  const selected = commitById(selectedId)!;
  const vars = extractVariables(selected.content);
  const lang = (state as any).exportLang as 'typescript' | 'python';
  const code =
    lang === 'typescript'
      ? generateTypeScript({
          promptName: repo.name,
          content: selected.content,
          variables: vars,
          version: selected.version,
          commitMessage: selected.message,
          exportedAt: Date.now(),
        })
      : generatePython({
          promptName: repo.name,
          content: selected.content,
          variables: vars,
          version: selected.version,
          commitMessage: selected.message,
          exportedAt: Date.now(),
        });
  const filename = exportFilename(lang, repo.name);

  return `
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div class="panel p-4 lg:col-span-1 flex flex-col gap-3">
        <h3 class="text-sm font-semibold text-white">Export settings</h3>
        <div>
          <label class="label">Commit version</label>
          <select id="exportCommit" class="field">
            ${commits.map((c) => `<option value="${c.id}" ${c.id === selectedId ? 'selected' : ''}>v${c.version} — ${escapeHtml(c.message)}</option>`).join('')}
          </select>
        </div>
        <div>
          <label class="label">Language</label>
          <select id="exportLang" class="field">
            <option value="typescript" ${lang === 'typescript' ? 'selected' : ''}>TypeScript (prompts.ts)</option>
            <option value="python" ${lang === 'python' ? 'selected' : ''}>Python (prompts.py)</option>
          </select>
        </div>
        <div class="text-xs text-slate-400">Variables: ${vars.length ? vars.map((v) => `{{${escapeHtml(v)}}}`).join(' ') : 'none'}</div>
        <div class="flex flex-wrap gap-2">
          <button id="btnCopyExport" class="btn-ghost" ${isPro() ? '' : 'disabled'}>Copy</button>
          <button id="btnDownloadExport" class="btn-primary" ${isPro() ? '' : 'disabled'}>Download ${escapeHtml(filename)}</button>
        </div>
        ${isPro() ? '' : '<p class="text-xs text-rose-300">Export is a Pro feature. Activate a license to unlock downloads.</p>'}
      </div>
      <div class="panel p-4 lg:col-span-2">
        <h3 class="text-sm font-semibold text-white mb-2">Generated code</h3>
        <div class="text-xs text-slate-500 mb-2">${escapeHtml(filename)}</div>
        <pre id="exportPreview" class="code-block scroll-y max-h-[560px] rounded-lg border border-slate-800 bg-slate-950/60 p-4">${escapeHtml(code)}</pre>
      </div>
    </div>
  `;
}

function modalHtml(): string {
  if (!state.modal) return '';
  if (state.modal === 'new-repo') {
    return `
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div class="panel w-full max-w-md p-5">
          <h3 class="text-lg font-semibold text-white mb-3">New prompt repo</h3>
          <div class="space-y-3">
            <div>
              <label class="label">Name</label>
              <input id="newRepoName" class="field" placeholder="Support Triage" />
            </div>
            <div>
              <label class="label">Description</label>
              <input id="newRepoDesc" class="field" placeholder="optional" />
            </div>
          </div>
          <div class="flex justify-end gap-2 mt-4">
            <button id="btnCancelModal" class="btn-ghost">Cancel</button>
            <button id="btnCreateRepo" class="btn-primary">Create</button>
          </div>
        </div>
      </div>
    `;
  }
  if (state.modal === 'provider') {
    const s = state.settings;
    return `
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div class="panel w-full max-w-lg p-5">
          <h3 class="text-lg font-semibold text-white mb-3">Provider settings</h3>
          <div class="space-y-3">
            <div>
              <label class="label">Active provider</label>
              <select id="provProvider" class="field">
                <option value="mock" ${s.provider === 'mock' ? 'selected' : ''}>Mock / Simulation (offline)</option>
                <option value="openai" ${s.provider === 'openai' ? 'selected' : ''}>OpenAI</option>
                <option value="anthropic" ${s.provider === 'anthropic' ? 'selected' : ''}>Anthropic</option>
                <option value="gemini" ${s.provider === 'gemini' ? 'selected' : ''}>Google Gemini</option>
              </select>
            </div>
            <div>
              <label class="label">OpenAI API key</label>
              <input id="provOpenaiKey" type="password" class="field" value="${escapeHtml(s.apiKeys.openai)}" placeholder="sk-..." />
            </div>
            <div>
              <label class="label">Anthropic API key</label>
              <input id="provAnthropicKey" type="password" class="field" value="${escapeHtml(s.apiKeys.anthropic)}" placeholder="sk-ant-..." />
            </div>
            <div>
              <label class="label">Gemini API key</label>
              <input id="provGeminiKey" type="password" class="field" value="${escapeHtml(s.apiKeys.gemini)}" placeholder="AIza..." />
            </div>
            <div>
              <label class="label">OpenAI model</label>
              <select id="provOpenaiModel" class="field">
                ${['gpt-5', 'gpt-5-mini', 'gpt-4.1', 'gpt-4.1-mini', 'gpt-4o', 'gpt-4o-mini'].map((m) => `<option value="${m}" ${s.models.openai === m ? 'selected' : ''}>${m}</option>`).join('')}
              </select>
            </div>
            <div>
              <label class="label">Anthropic model</label>
              <select id="provAnthropicModel" class="field">
                ${['claude-opus-4-20250514', 'claude-sonnet-4-20250514', 'claude-3-7-sonnet-latest', 'claude-3-5-haiku-latest'].map((m) => `<option value="${m}" ${s.models.anthropic === m ? 'selected' : ''}>${m}</option>`).join('')}
              </select>
            </div>
            <div>
              <label class="label">Gemini model</label>
              <select id="provGeminiModel" class="field">
                ${['gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.5-flash-lite'].map((m) => `<option value="${m}" ${s.models.gemini === m ? 'selected' : ''}>${m}</option>`).join('')}
              </select>
            </div>
            <div>
              <label class="label">Default output tokens</label>
              <input id="provOutputTokens" type="number" min="0" class="field" value="${s.outputTokens}" />
            </div>
            <label class="flex items-center gap-2 text-sm text-slate-300">
              <input id="provSimLatency" type="checkbox" ${s.simulateLatency ? 'checked' : ''} />
              Simulate latency in mock mode
            </label>
          </div>
          <div class="flex justify-end gap-2 mt-4">
            <button id="btnCancelModal" class="btn-ghost">Cancel</button>
            <button id="btnSaveProvider" class="btn-primary">Save</button>
          </div>
        </div>
      </div>
    `;
  }
  if (state.modal === 'license') {
    return `
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div class="panel w-full max-w-md p-5">
          <h3 class="text-lg font-semibold text-white mb-3">License</h3>
          <p class="text-sm text-slate-400 mb-3">Current tier: <strong class="text-white">${state.license.tier}</strong>. Enter any activation key to unlock Pro (offline, for demo).</p>
          <div>
            <label class="label">Activation key</label>
            <input id="licenseKey" class="field" placeholder="PV-XXXX-XXXX" />
          </div>
          <div class="flex justify-end gap-2 mt-4">
            <button id="btnDeactivateLicense" class="btn-danger">Deactivate</button>
            <button id="btnCancelModal" class="btn-ghost">Cancel</button>
            <button id="btnActivateLicense" class="btn-primary">Activate Pro</button>
          </div>
        </div>
      </div>
    `;
  }
  return '';
}

function toastHtml(): string {
  if (!state.toast) return '';
  const color =
    state.toast.kind === 'success' ? 'border-emerald-500/40 text-emerald-200' : state.toast.kind === 'error' ? 'border-rose-500/40 text-rose-200' : 'border-slate-600 text-slate-200';
  return `
    <div class="fixed bottom-4 right-4 z-50 panel px-4 py-2 text-sm ${color}">${escapeHtml(state.toast.message)}</div>
  `;
}

function wire(): void {
  maybe<HTMLSelectElement>('#repoSelect')?.addEventListener('change', (e) => {
    state.selectedRepoId = (e.target as HTMLSelectElement).value || null;
    state.editorBaseCommitId = null;
    saveState();
    render();
  });

  maybe<HTMLButtonElement>('#btnNewRepo')?.addEventListener('click', () => openModal('new-repo'));
  maybe<HTMLButtonElement>('#btnEmptyNewRepo')?.addEventListener('click', () => openModal('new-repo'));
  maybe<HTMLButtonElement>('#btnProvider')?.addEventListener('click', () => openModal('provider'));
  maybe<HTMLButtonElement>('#btnLicense')?.addEventListener('click', () => openModal('license'));

  document.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach((btn) => {
    btn.addEventListener('click', () => setActiveTab(btn.dataset.tab as TabId));
  });

  maybe<HTMLButtonElement>('#btnCancelModal')?.addEventListener('click', closeModal);

  if (state.modal === 'new-repo') wireNewRepoModal();
  if (state.modal === 'provider') wireProviderModal();
  if (state.modal === 'license') wireLicenseModal();

  if (state.activeTab === 'editor') wireEditor();
  if (state.activeTab === 'history') wireHistory();
  if (state.activeTab === 'estimator') wireEstimator();
  if (state.activeTab === 'tests') wireTests();
  if (state.activeTab === 'export') wireExport();
}

function wireNewRepoModal(): void {
  qs<HTMLButtonElement>('#btnCreateRepo').addEventListener('click', () => {
    const name = (qs<HTMLInputElement>('#newRepoName').value || '').trim();
    const desc = (qs<HTMLInputElement>('#newRepoDesc').value || '').trim();
    if (!name) {
      toast('Repo name is required', 'error');
      return;
    }
    if (!isPro() && state.repos.length >= 3) {
      toast('Free tier limited to 3 repos. Upgrade to Pro.', 'error');
      return;
    }
    const now = Date.now();
    const repo: PromptRepo = { id: uid('repo'), name, description: desc, createdAt: now, updatedAt: now };
    const commit: Commit = {
      id: uid('commit'),
      repoId: repo.id,
      version: 1,
      message: 'Initial commit',
      content: STARTER_PROMPT,
      createdAt: now,
    };
    state.repos.push(repo);
    state.commits.push(commit);
    state.selectedRepoId = repo.id;
    state.editorContent = commit.content;
    state.editorBaseCommitId = commit.id;
    state.editorMessage = 'Working copy';
    state.modal = null;
    saveState();
    toast(`Created repo "${name}"`, 'success');
    render();
  });
}

function wireProviderModal(): void {
  qs<HTMLButtonElement>('#btnSaveProvider').addEventListener('click', () => {
    const s = state.settings;
    s.provider = qs<HTMLSelectElement>('#provProvider').value as Settings['provider'];
    s.apiKeys.openai = qs<HTMLInputElement>('#provOpenaiKey').value;
    s.apiKeys.anthropic = qs<HTMLInputElement>('#provAnthropicKey').value;
    s.apiKeys.gemini = qs<HTMLInputElement>('#provGeminiKey').value;
    s.models.openai = qs<HTMLSelectElement>('#provOpenaiModel').value;
    s.models.anthropic = qs<HTMLSelectElement>('#provAnthropicModel').value;
    s.models.gemini = qs<HTMLSelectElement>('#provGeminiModel').value;
    s.outputTokens = Number(qs<HTMLInputElement>('#provOutputTokens').value) || s.outputTokens;
    s.simulateLatency = qs<HTMLInputElement>('#provSimLatency').checked;
    state.estimatorOutputTokens = s.outputTokens;
    saveState();
    closeModal();
    toast('Provider settings saved', 'success');
  });
}

function wireLicenseModal(): void {
  qs<HTMLButtonElement>('#btnActivateLicense').addEventListener('click', () => {
    const key = qs<HTMLInputElement>('#licenseKey').value.trim();
    if (!key) {
      toast('Enter an activation key', 'error');
      return;
    }
    state.license = { tier: 'pro', kind: 'lifetime', key, activatedAt: Date.now(), expiresAt: null };
    saveState();
    closeModal();
    toast('Pro license activated', 'success');
  });
  maybe<HTMLButtonElement>('#btnDeactivateLicense')?.addEventListener('click', () => {
    state.license = { tier: 'free' };
    saveState();
    closeModal();
    toast('License deactivated', 'info');
  });
}

function wireEditor(): void {
  const contentEl = qs<HTMLTextAreaElement>('#editorContent');
  const messageEl = qs<HTMLInputElement>('#editorMessage');
  const previewEl = qs<HTMLDivElement>('#editorPreview');
  const tokenEl = maybe<HTMLDivElement>('#editorTokenSummary');
  const varsEl = maybe<HTMLDivElement>('#editorVars');
  const valuesEl = maybe<HTMLTextAreaElement>('#editorTestValues');
  const previewValsEl = maybe<HTMLDivElement>('#editorPreviewVals');

  function refresh(): void {
    state.editorContent = contentEl.value;
    const ts = tokenStats(state.editorContent);
    if (tokenEl) tokenEl.textContent = `Tokens: ${ts.tokens} · Chars: ${ts.chars} · Words: ${ts.words} · Lines: ${ts.lines}`;
    const vars = extractVariables(state.editorContent);
    if (varsEl) varsEl.innerHTML = vars.map((v) => chip(`{{${v}}}`)).join(' ');
    if (previewEl) previewEl.textContent = interpolatePreview(state.editorContent).output;
  }

  contentEl.addEventListener('input', refresh);
  refresh();

  qs<HTMLButtonElement>('#btnSaveCommit').addEventListener('click', () => {
    const repo = selectedRepo();
    if (!repo) return;
    const content = contentEl.value;
    const message = messageEl.value.trim() || 'Update prompt';
    const commits = commitsFor(repo.id);
    const nextVersion = (commits.length ? commits[commits.length - 1].version : 0) + 1;
    const commit: Commit = {
      id: uid('commit'),
      repoId: repo.id,
      version: nextVersion,
      message,
      content,
      createdAt: Date.now(),
    };
    state.commits.push(commit);
    repo.updatedAt = Date.now();
    state.editorContent = content;
    state.editorMessage = 'Working copy';
    state.editorBaseCommitId = commit.id;
    saveState();
    toast(`Committed v${nextVersion}`, 'success');
    render();
  });

  qs<HTMLButtonElement>('#btnResetEditor').addEventListener('click', () => {
    const repo = selectedRepo();
    const lc = repo ? latestCommit(repo.id) : undefined;
    state.editorContent = lc?.content ?? '';
    contentEl.value = state.editorContent;
    refresh();
  });

  maybe<HTMLButtonElement>('#btnPreviewWithValues')?.addEventListener('click', () => {
    if (!valuesEl || !previewValsEl) return;
    let vals: Record<string, string> = {};
    try {
      vals = JSON.parse(valuesEl.value || '{}');
    } catch {
      toast('Invalid JSON values', 'error');
      return;
    }
    previewValsEl.textContent = interpolate(state.editorContent, vals).output;
  });
}

function wireHistory(): void {
  const left = qs<HTMLSelectElement>('#historyLeft');
  const right = qs<HTMLSelectElement>('#historyRight');

  const update = (): void => {
    state.historyLeftId = left.value;
    state.historyRightId = right.value;
    const l = commitById(left.value);
    const r = commitById(right.value);
    const viewer = maybe<HTMLDivElement>('#diffViewer');
    if (!viewer || !l || !r) return;
    const ops = diffWords(l.content, r.content);
    const stats = diffStats(ops);
    viewer.innerHTML = `<div class="code-block">${renderDiffHtml(ops)}</div>`;
    void stats;
  };

  left.addEventListener('change', update);
  right.addEventListener('change', update);
  qs<HTMLButtonElement>('#btnRenderDiff').addEventListener('click', update);
}

function wireEstimator(): void {
  const promptEl = qs<HTMLTextAreaElement>('#estimatorPrompt');
  const outputEl = qs<HTMLInputElement>('#estimatorOutput');
  const breakdownEl = maybe<HTMLDivElement>('#estimatorBreakdown');
  const tableEl = maybe<HTMLDivElement>('#estimatorTable');

  const refresh = (): void => {
    const text = promptEl.value;
    const inputTokens = estimateTokens(text);
    const outputTokens = Math.max(0, Number(outputEl.value) || 0);
    state.estimatorOutputTokens = outputTokens;
    const ts = tokenStats(text);
    if (breakdownEl) {
      breakdownEl.innerHTML = `
        <div class="flex flex-wrap gap-2">
          ${chip(`input tokens ${inputTokens}`)}
          ${chip(`chars ${ts.chars}`)}
          ${chip(`words ${ts.words}`)}
          ${chip(`lines ${ts.lines}`)}
        </div>
      `;
    }
    if (tableEl) {
      const rows = buildCostTable(inputTokens, outputTokens);
      tableEl.innerHTML = `
        <table class="w-full">
          <thead><tr>
            <th class="table-th">Model</th>
            <th class="table-th">Tier</th>
            <th class="table-th">Per 1K calls</th>
            <th class="table-th">Per 100K calls/mo</th>
          </tr></thead>
          <tbody>
            ${rows
              .map(
                (row) => `<tr>
                  <td class="table-td font-medium">${escapeHtml(row.model.label)} <span class="text-xs text-slate-500">${escapeHtml(row.model.providerLabel)}</span></td>
                  <td class="table-td text-xs">${escapeHtml(row.model.tier)}</td>
                  <td class="table-td font-mono">${escapeHtml(formatUsd(row.per1k))}</td>
                  <td class="table-td font-mono">${escapeHtml(formatUsdFull(row.per100k))}</td>
                </tr>`,
              )
              .join('')}
          </tbody>
        </table>
      `;
    }
  };

  promptEl.addEventListener('input', refresh);
  outputEl.addEventListener('input', refresh);
  refresh();
}

function wireTests(): void {
  maybe<HTMLButtonElement>('#btnAddAssertion')?.addEventListener('click', () => {
    const type = qs<HTMLSelectElement>('#assertionType').value as 'json' | 'json-keys' | 'not-regex' | 'word-count';
    let spec = makeAssertion(type);
    if (type === 'json-keys') {
      const raw = window.prompt('Comma-separated required keys', 'answer,reason');
      if (raw !== null) spec = makeAssertion(type, { keys: raw.split(',').map((s) => s.trim()).filter(Boolean) });
    } else if (type === 'not-regex') {
      const raw = window.prompt('Forbidden regex (e.g. /sorry|as an AI/i)', '/sorry|as an AI|cannot/i');
      if (raw !== null) spec = makeAssertion(type, { pattern: raw });
    } else if (type === 'word-count') {
      const minRaw = window.prompt('Minimum word count', '5');
      const maxRaw = window.prompt('Maximum word count', '120');
      spec = makeAssertion(type, { min: Number(minRaw) || 0, max: Number(maxRaw) || 120 });
    }
    state.draftAssertions.push(spec);
    const list = maybe<HTMLDivElement>('#assertionList');
    if (list) {
      list.innerHTML = state.draftAssertions
        .map((a, i) => `<div class="chip" data-idx="${i}">${escapeHtml(a.type)} <button class="text-rose-300 ml-1" data-remove-assertion="${i}">×</button></div>`)
        .join(' ') || '<span class="text-xs text-slate-500">No assertions attached yet.</span>';
    }
  });

  document.querySelectorAll<HTMLButtonElement>('[data-remove-case]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.removeCase!;
      state.cases = state.cases.filter((c) => c.id !== id);
      saveState();
      render();
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-remove-assertion]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx = Number(btn.dataset.removeAssertion);
      state.draftAssertions.splice(idx, 1);
      render();
    });
  });

  maybe<HTMLButtonElement>('#btnAddCase')?.addEventListener('click', () => {
    const repo = selectedRepo();
    if (!repo) return;
    if (!isPro() && casesFor(repo.id).length >= 5) {
      toast('Free tier limited to 5 test cases per prompt.', 'error');
      return;
    }
    const nameInput = qs<HTMLInputElement>('#caseName');
    const name = nameInput.value.trim() || `Case ${casesFor(repo.id).length + 1}`;
    const valueInputs = document.querySelectorAll<HTMLInputElement>('.test-var');
    const values: Record<string, string> = {};
    valueInputs.forEach((inp) => {
      const key = inp.dataset.var;
      if (key) values[key] = inp.value;
    });
    const newCase: TestCase = {
      id: uid('case'),
      repoId: repo.id,
      name,
      values,
      assertions: [...state.draftAssertions],
      createdAt: Date.now(),
    };
    state.cases.push(newCase);
    state.draftAssertions = [];
    saveState();
    toast('Test case added', 'success');
    render();
  });

  maybe<HTMLButtonElement>('#btnRunTests')?.addEventListener('click', async () => {
    const repo = selectedRepo();
    if (!repo) return;
    const cases = casesFor(repo.id);
    if (!cases.length) {
      toast('Add at least one test case first.', 'error');
      return;
    }
    const commits = commitsFor(repo.id);
    const runCommitId = qs<HTMLSelectElement>('#runCommit').value;
    const runCommit = commits.find((c) => c.id === runCommitId);
    if (!runCommit) {
      toast('Select a commit', 'error');
      return;
    }
    const compare = maybe<HTMLInputElement>('#runCompare')?.checked ?? false;
    const selectedCommits = [runCommit];
    if (compare && isPro()) {
      const idx = commits.findIndex((c) => c.id === runCommit.id);
      const prev = idx > 0 ? commits[idx - 1] : undefined;
      if (prev) selectedCommits.unshift(prev);
    }

    state.running = true;
    state.runProgress = { completed: 0, total: selectedCommits.length * cases.length };
    render();
    try {
      const results = await runSuite({
        repoId: repo.id,
        commits: selectedCommits,
        cases,
        settings: state.settings,
        delayMs: state.settings.simulateLatency ? undefined : 0,
        onProgress: (completed, total) => {
          state.runProgress = { completed, total };
          const el = maybe<HTMLSpanElement>('span.text-indigo-300');
          if (el) el.textContent = `Running… ${completed}/${total}`;
        },
      });
      state.testResults = results;
      toast('Test run complete', 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Run failed', 'error');
    } finally {
      state.running = false;
      state.runProgress = null;
      saveState();
      render();
    }
  });
}

function wireExport(): void {
  const commitSel = qs<HTMLSelectElement>('#exportCommit');
  const langSel = qs<HTMLSelectElement>('#exportLang');

  (state as any).exportCommitId = commitSel.value;
  (state as any).exportLang = langSel.value;

  const update = (): void => {
    (state as any).exportCommitId = commitSel.value;
    (state as any).exportLang = langSel.value;
    render();
  };
  commitSel.addEventListener('change', update);
  langSel.addEventListener('change', update);

  const code = qs<HTMLPreElement>('#exportPreview').textContent ?? '';
  const repo = selectedRepo()!;
  const filename = exportFilename(((state as any).exportLang as string) === 'python' ? 'python' : 'typescript', repo.name);

  maybe<HTMLButtonElement>('#btnCopyExport')?.addEventListener('click', async () => {
    if (!isPro()) {
      toast('Pro required for export', 'error');
      return;
    }
    const ok = await copyToClipboard(code);
    toast(ok ? 'Copied to clipboard' : 'Copy failed', ok ? 'success' : 'error');
  });

  maybe<HTMLButtonElement>('#btnDownloadExport')?.addEventListener('click', () => {
    if (!isPro()) {
      toast('Pro required for export', 'error');
      return;
    }
    downloadFile(filename, code, 'text/plain');
    toast('Download started', 'success');
  });
}

(state as any).exportCommitId = null;
(state as any).exportLang = 'typescript';

loadState();
if (!(state as any).exportCommitId) (state as any).exportCommitId = null;
render();
export {};
